const KeenSlider = window.KeenSlider
const { debounce } = window.BAO.Utils
const { setInitialised, useDots, useArrows } = window.BAO.Carousel.plugins

class ProductImageCarousel extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['slides', 'slide']
  }

  constructor () {
    super()
    this.isDesktop = false // Initialise with default value
    this.isMobile = false // Initialise with default value
    this.handleResize = debounce(this.handleResize.bind(this), 200) // Debounce handleResize method
  }

  connectedCallback () {
    super.connectedCallback()
    this.updateIsDesktop()
    this.updateIsMobile()
    this.setGalleryHeight()
    this.setupCarousel()
    this.setupThumbnailCarousel()
    window.addEventListener('resize', this.handleResize)
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.handleResize)
    this.destroyCarousel()
  }

  setupListeners () {
    super.setupListeners()

    if (!this.externalEls?.thumbnails?.exists) return

    this.handleThumbnailTrigger = this.handleThumbnailTrigger.bind(this)

    this.externalEls?.thumbnail.elements.forEach(thumbnailtriggerEl => {
      this.listeners.add(
        thumbnailtriggerEl,
        'click',
        this.handleThumbnailTrigger,
      )
    })
  }

  setGalleryHeight () {
    const galleryHeight = `${this.els?.slide?.element.offsetHeight}px`

    this.externalEls?.wrapper?.element.style.setProperty(
      '--GalleryHeight',
      galleryHeight,
    )
  }

  /**
   * Sets up the carousel if the device is not desktop and there's more than one slide.
   */
  setupCarousel () {
    const slideCount = this.els.slides.element.children.length // Get the number of slides
    this.onSlideChanged = this.onSlideChanged.bind(this)

    if (slideCount <= 1) {
      this.setAttribute('data-initialised', 'false')
      return
    }

    const dots = Array.from(this.els.dots.element.children) // Get all the dot elements
    const arrows = {
      container: this.els?.arrows?.element,
      previous: this.els?.previous?.element,
      next: this.els?.next?.element,
    }

    this.carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        loop: true,
        mode: 'snap',
        rubberband: false,
        slides: {
          perView: 1,
          spacing: 2,
        },
        slideChanged: this.onSlideChanged,
        dragStarted: () => {
          this.els.slides.element.classList.add('pga-Gallery_Slides-dragging')
        },
        dragEnded: () => {
          this.els.slides.element.classList.remove(
            'pga-Gallery_Slides-dragging',
          )
        },
      },
      [setInitialised(), useDots(dots), useArrows(arrows)],
    )

    this.setAttribute('data-initialised', 'true')
  }

  setupThumbnailCarousel () {
    if (!this.externalEls?.thumbnails?.exists) return

    this.thumbnailCarousel = new KeenSlider(
      this.externalEls.thumbnails.element,
      {
        selector: this.externalEls.thumbnail.selector,
        loop: false,
        mode: 'snap',
        rubberband: false,
        vertical: true,
        slides: {
          perView: 'auto',
          spacing: 2,
        },
        dragStarted: () => {
          this.externalEls.thumbnails.element.classList.add(
            'pga-GalleryThumbnail_Slides-dragging',
          )
        },
        dragEnded: () => {
          this.externalEls.thumbnails.element.classList.remove(
            'pga-GalleryThumbnail_Slides-dragging',
          )
        },
      },
      [setInitialised()],
    )

    this.externalEls.thumbnails.element.setAttribute('data-initialised', 'true')
  }

  handleThumbnailTrigger (e) {
    const thumbnailTrigger = e.target.closest(
      `${this.externalEls?.thumbnail.selector}`,
    )

    const targetIndex = parseInt(thumbnailTrigger.dataset.index, 10)

    this.carousel.moveToIdx(targetIndex)
  }

  onSlideChanged (instance) {
    if (!this.externalEls?.thumbnails?.exists) return

    const currentSlide = instance.track.details.rel

    this.externalEls.thumbnail.elements.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-current', index === currentSlide)
    })
  }

  /**
   * Destroys the carousel if it exists.
   */
  destroyCarousel () {
    if (this.carousel) {
      this.carousel.destroy()
      this.carousel = null

      this.setAttribute('data-initialised', 'false')
    }
  }

  destroyThumbnailCarousel () {
    if (this.thumbnailCarousel) {
      this.thumbnailCarousel.destroy()
      this.thumbnailCarousel = null

      this.externalEls.thumbnails.element.setAttribute(
        'data-initialised',
        'false',
      )
    }
  }

  /**
   * Checks if the device is in desktop mode and updates the isDesktop flag accordingly.
   * @returns {boolean} True if the value of isDesktop has changed, false otherwise.
   */
  updateIsDesktop () {
    const isDesktop = window.innerWidth >= 901

    if (isDesktop !== this.isDesktop) {
      this.isDesktop = isDesktop
      return true // Indicates the value changed
    }
    return false // Value remains the same
  }

  updateIsMobile () {
    const isDesktop = window.innerWidth >= 901
    const isMobile = window.innerWidth <= 900

    if (isDesktop !== this.isDesktop) {
      this.isDesktop = isDesktop
      return true // Indicates the value changed
    }

    if (isMobile !== this.isMobile) {
      this.isMobile = isMobile
      return true // Indicates the value changed
    }
    return false // Value remains the same
  }

  /**
   * Handles the window resize event, updating the carousel setup if necessary.
   */
  handleResize () {
    if (this.updateIsMobile()) {
      if (!this.isMobile && !this.carousel) {
        this.setupThumbnailCarousel()
      } else if (this.isMobile && this.carousel) {
        this.destroyThumbnailCarousel()
      }
    }

    if (this.updateIsDesktop()) {
      if (!this.isDesktop && !this.carousel) {
        this.setupCarousel()
      } else if (this.isDesktop && this.carousel) {
        this.destroyCarousel()
      }
    }

    this.setGalleryHeight()
  }
}

if (!customElements.get('product-image-carousel')) {
  customElements.define('product-image-carousel', ProductImageCarousel)
}
